<template>
  <div class="hierarchy-panel-container">
    <div class="row no-wrap" style="height: 100%;">
      <!-- Left Side: Tree View -->
      <div :class="shouldShowDetailsSidebar ? 'col-8' : 'col'" class="hierarchy-tree-section">
        <div class="row items-center justify-between q-mb-md">
          <div class="text-h6">Site Hierarchy</div>
          <q-btn
            color="primary"
            icon="business"
            label="Add Building"
            @click="emit('addBuilding')"
          />
        </div>

        <!-- Brick Statistics Panel -->
        <BrickStatsPanel :hierarchy-stats="props.hierarchyStats" />

        <!-- Site hierarchy tree view -->
        <q-tree
          :nodes="props.treeNodes"
          node-key="id"
          :expanded="props.expandedNodes"
          no-connectors
          :selected="props.selectedNode"
          @update:expanded="emit('update:expandedNodes', $event)"
          @update:selected="emit('update:selectedNode', $event)"
        >
          <template #default-header="prop">
            <div
              class="row items-center full-width"
              :class="{
                'device-reference-node': prop.node.type === 'device-reference',
                'other-quote-equipment': !belongsToCurrentQuote(prop.node)
              }"
              @contextmenu.prevent="emit('contextMenu', { event: $event, node: prop.node })"
            >
              <q-icon :name="prop.node.icon" class="q-mr-sm" :color="prop.node.color" />
              <div class="col">
                <div class="row items-center">
                  <div class="text-weight-bold">
                    {{ prop.node.label }}
                    <!-- Tooltip for device reference nodes (dual-location feature) -->
                    <q-tooltip v-if="prop.node.type === 'device-reference'" anchor="top middle" self="bottom middle">
                      {{ prop.node.data?.locationDisplay || 'Physical location reference' }}
                    </q-tooltip>
                  </div>
                  <!-- Compound Device Badge -->
                  <q-badge
                    v-if="isCompoundDevice(prop.node)"
                    color="purple"
                    outline
                    class="q-ml-sm"
                  >
                    <q-icon name="hub" size="xs" class="q-mr-xs" />
                    Compound
                    <q-tooltip>
                      {{ getCompoundDeviceTooltip(prop.node) }}
                    </q-tooltip>
                  </q-badge>
                  <!-- Template Badge -->
                  <q-badge
                    v-if="isTemplatedEquipment(prop.node) || isTemplatedDevice(prop.node)"
                    :color="hasTemplateUpdate(prop.node) ? 'orange' : 'blue'"
                    outline
                    class="q-ml-sm"
                  >
                    <q-icon name="link" size="xs" class="q-mr-xs" />
                    {{ hasTemplateUpdate(prop.node) ? 'Update Available' : 'Template' }}
                    <q-tooltip>
                      {{ getTemplateTooltip(prop.node) }}
                    </q-tooltip>
                  </q-badge>
                  <!-- Lifecycle Stage Badge -->
                  <q-badge
                    v-if="prop.node.type === 'equipment' && prop.node.data?.lifecycle_stage"
                    :color="getLifecycleColor(prop.node.data.lifecycle_stage)"
                    class="q-ml-sm"
                  >
                    <q-icon :name="getLifecycleIcon(prop.node.data.lifecycle_stage)" size="xs" class="q-mr-xs" />
                    {{ getLifecycleLabel(prop.node.data.lifecycle_stage) }}
                    <q-tooltip>
                      Lifecycle Stage: {{ prop.node.data.lifecycle_stage }}
                      <span v-if="prop.node.data.quote_id"> (Quote #{{ prop.node.data.quote_id }})</span>
                    </q-tooltip>
                  </q-badge>
                  <!-- Replacement Badge -->
                  <q-badge
                    v-if="prop.node.type === 'equipment' && prop.node.data?.replaced_by_equipment_id"
                    color="red"
                    outline
                    class="q-ml-sm"
                  >
                    <q-icon name="swap_horiz" size="xs" class="q-mr-xs" />
                    Replacement
                    <q-tooltip>
                      This equipment is being replaced (ID: {{ prop.node.data.replaced_by_equipment_id }})
                    </q-tooltip>
                  </q-badge>
                  <!-- Documentation Status Badge for Equipment (excluding Zone Sections) -->
                  <q-chip
                    v-if="prop.node.type === 'equipment' && !isZoneSection(prop.node)"
                    :color="getDocumentationStatusColor(prop.node)"
                    text-color="white"
                    size="sm"
                    :icon="getDocumentationStatusIcon(prop.node)"
                    class="q-ml-sm"
                    dense
                  >
                    {{ getDocumentationStatus(prop.node) }}
                  </q-chip>
                  <!-- Space Summary Badge -->
                  <q-chip
                    v-if="shouldShowSpaceBadge(prop.node)"
                    :color="getSpaceSummaryChipColor(prop.node)"
                    text-color="white"
                    size="sm"
                    :icon="getSpaceSummaryIcon(prop.node)"
                    class="q-ml-sm"
                    dense
                  >
                    {{ getSpaceSummaryText(prop.node) }}
                  </q-chip>
                  <!-- Space Metadata Badge -->
                  <q-badge
                    v-if="prop.node.type === 'space'"
                    :color="getSpaceMetadataBadgeColor(prop.node.data)"
                    :label="getSpaceMetadataBadgeLabel(prop.node.data)"
                    class="q-ml-xs"
                    outline
                  >
                    <q-tooltip>{{ getSpaceMetadataTooltip(prop.node.data) }}</q-tooltip>
                  </q-badge>
                  <!-- Info Button for Spaces (inline) -->
                  <q-btn
                    v-if="prop.node.type === 'space'"
                    size="xs"
                    round
                    flat
                    dense
                    color="primary"
                    icon="info"
                    class="q-ml-xs"
                    @click.stop="emit('showSpaceSummary', prop.node)"
                  >
                    <q-tooltip>View Space Summary</q-tooltip>
                  </q-btn>
                </div>
                <div class="text-caption text-grey">{{ prop.node.brickClass || 'No brick class' }}</div>
                <div v-if="isTemplatedEquipment(prop.node) || isTemplatedDevice(prop.node)" class="text-caption text-orange">
                  Template: {{ getTemplateInfo(prop.node)?.templateName || 'Unknown' }}
                  <span v-if="getTemplateInfo(prop.node)?.isFromParent" class="text-grey-6"> (from parent)</span>
                </div>
              </div>
              <div class="q-gutter-xs">
                <!-- Building Actions -->
                <q-btn
                  v-if="prop.node.type === 'building'"
                  size="sm"
                  round
                  color="primary"
                  icon="add"
                  @click.stop="emit('addLevel', prop.node)"
                >
                  <q-tooltip>Add Level</q-tooltip>
                </q-btn>
                <!-- Level Actions -->
                <q-btn
                  v-if="prop.node.type === 'level'"
                  size="sm"
                  round
                  color="secondary"
                  icon="add"
                  @click.stop="emit('addSpace', prop.node)"
                >
                  <q-tooltip>Add Space</q-tooltip>
                </q-btn>
                <!-- Space Actions (Add buttons remain) -->
                <q-btn
                  v-if="prop.node.type === 'space'"
                  size="sm"
                  round
                  color="accent"
                  icon="precision_manufacturing"
                  @click.stop="emit('addEquipmentToSpace', prop.node)"
                >
                  <q-tooltip>Add Equipment</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="prop.node.type === 'space'"
                  size="sm"
                  round
                  color="red"
                  icon="electrical_services"
                  @click.stop="emit('addSwitchboardToSpace', prop.node)"
                >
                  <q-tooltip>Add Switchboard</q-tooltip>
                </q-btn>

                <!-- Controller Actions (equipment with controller_template_id or brick_class containing 'Controller') -->
                <q-btn
                  v-if="prop.node.type === 'equipment' && isController(prop.node)"
                  size="sm"
                  round
                  color="blue-7"
                  icon="memory"
                  @click.stop="emit('showControllerIoDialog', prop.node)"
                >
                  <q-tooltip>View I/O</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="prop.node.type === 'equipment' && isController(prop.node)"
                  size="sm"
                  round
                  color="info"
                  icon="edit"
                  @click.stop="emit('editEquipment', prop.node)"
                >
                  <q-tooltip>Edit Controller</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="prop.node.type === 'equipment' && isController(prop.node)"
                  size="sm"
                  round
                  color="negative"
                  icon="delete"
                  @click.stop="emit('deleteEquipment', prop.node)"
                >
                  <q-tooltip>Delete Controller</q-tooltip>
                </q-btn>

                <!-- Zone Section Actions -->
                <q-btn
                  v-if="prop.node.type === 'zoneSection'"
                  size="sm"
                  round
                  color="accent"
                  icon="memory"
                  @click.stop="emit('addDeviceToZone', prop.node)"
                >
                  <q-tooltip>Add Device</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="prop.node.type === 'zoneSection'"
                  size="sm"
                  round
                  color="cyan"
                  icon="sensors"
                  @click.stop="emit('addPointToZone', prop.node)"
                >
                  <q-tooltip>Add Point</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="prop.node.type === 'zoneSection'"
                  size="sm"
                  round
                  color="info"
                  icon="edit"
                  @click.stop="emit('editEquipment', prop.node)"
                >
                  <q-tooltip>Edit</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="prop.node.type === 'zoneSection'"
                  size="sm"
                  round
                  color="teal"
                  icon="save_as"
                  @click.stop="emit('saveZoneAsTemplate', prop.node)"
                >
                  <q-tooltip>Save as Template</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="prop.node.type === 'zoneSection'"
                  size="sm"
                  round
                  color="purple"
                  icon="content_copy"
                  @click.stop="emit('duplicateZone', prop.node)"
                >
                  <q-tooltip>Duplicate</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="prop.node.type === 'zoneSection'"
                  size="sm"
                  round
                  color="negative"
                  icon="delete"
                  @click.stop="emit('confirmDelete', prop.node)"
                >
                  <q-tooltip>Delete</q-tooltip>
                </q-btn>
                <!-- Equipment Actions -->
                <q-btn
                  v-if="prop.node.type === 'equipment' && !isController(prop.node) && (!isTemplatedEquipment(prop.node) || isZoneSection(prop.node))"
                  size="sm"
                  round
                  color="info"
                  icon="edit"
                  @click.stop="emit('editEquipment', prop.node)"
                >
                  <q-tooltip>Edit Equipment</q-tooltip>
                </q-btn>
                <!-- Document Button for ALL Equipment (except Zone Sections) + Switchboards -->
                <q-btn
                  v-if="(prop.node.type === 'equipment' || prop.node.type === 'switchboard') && !isZoneSection(prop.node)"
                  size="sm"
                  round
                  :color="getDocumentationButtonColor(prop.node)"
                  icon="description"
                  @click.stop="emit('showDocumentation', prop.node)"
                >
                  <q-badge
                    v-if="getDocumentationPercentage(prop.node) !== null"
                    :color="getDocumentationBadgeColor(prop.node)"
                    floating
                    transparent
                  >
                    {{ getDocumentationPercentage(prop.node) }}%
                  </q-badge>
                  <q-tooltip>
                    <div>Documentation: {{ getDocumentationPercentage(prop.node) || 0 }}% Complete</div>
                    <div v-if="getDocumentationMissing(prop.node)" class="text-caption">
                      Missing: {{ getDocumentationMissing(prop.node) }}
                    </div>
                  </q-tooltip>
                </q-btn>
                <!-- Mark as Replacement Button (only in quote context for installed equipment) -->
                <q-btn
                  v-if="quoteContext && prop.node.type === 'equipment' && prop.node.data?.lifecycle_stage === 'installed'"
                  size="sm"
                  round
                  color="amber"
                  icon="swap_horiz"
                  @click.stop="emit('markAsReplacement', prop.node)"
                >
                  <q-tooltip>Mark as Replacement in Quote</q-tooltip>
                </q-btn>
                <!-- Orange Link Button for Templated Equipment -->
                <q-btn
                  v-if="prop.node.type === 'equipment' && !isController(prop.node) && isTemplatedEquipment(prop.node)"
                  size="sm"
                  round
                  color="orange"
                  icon="link"
                  @click.stop="emit('showTemplateActions', prop.node)"
                >
                  <q-tooltip>Template Actions</q-tooltip>
                </q-btn>

                <!-- Orange Link Button for Templated Devices -->
                <q-btn
                  v-if="prop.node.type === 'device' && isTemplatedDevice(prop.node)"
                  size="sm"
                  round
                  color="orange"
                  icon="link"
                  @click.stop="emit('showTemplateActions', prop.node)"
                >
                  <q-tooltip>Template Actions</q-tooltip>
                </q-btn>

                <!-- Add Device Button for Non-Templated Equipment (exclude Controllers) -->
                <q-btn
                  v-if="prop.node.type === 'equipment' && !isController(prop.node) && !isTemplatedEquipment(prop.node)"
                  size="sm"
                  round
                  color="secondary"
                  icon="memory"
                  @click.stop="emit('addDevice', prop.node)"
                >
                  <q-tooltip>Add Device</q-tooltip>
                </q-btn>

                <!-- Add Point Button for Non-Templated Equipment (exclude Controllers) -->
                <q-btn
                  v-if="prop.node.type === 'equipment' && !isController(prop.node) && !isTemplatedEquipment(prop.node) && canAddPointsToEquipment(prop.node)"
                  size="sm"
                  round
                  color="info"
                  icon="sensors"
                  @click.stop="emit('addPointToEquipment', prop.node)"
                >
                  <q-tooltip>Add Point</q-tooltip>
                </q-btn>

                <!-- Add Zone Section Button for AHUs (exclude Controllers) -->
                <q-btn
                  v-if="prop.node.type === 'equipment' && !isController(prop.node) && isAHU(prop.node) && !isZoneSection(prop.node) && !isTemplatedEquipment(prop.node)"
                  size="sm"
                  round
                  color="purple"
                  icon="dashboard_customize"
                  @click.stop="emit('addZoneSection', prop.node)"
                >
                  <q-tooltip>Add Internal Zone Section</q-tooltip>
                </q-btn>

                <!-- Device Actions -->
                <q-btn
                  v-if="prop.node.type === 'device' && !isTemplatedDevice(prop.node)"
                  size="sm"
                  round
                  color="info"
                  icon="edit"
                  @click.stop="emit('editDevice', prop.node)"
                >
                  <q-tooltip>Edit Device</q-tooltip>
                </q-btn>
                <!-- Add Point Button for Non-Templated Devices -->
                <q-btn
                  v-if="prop.node.type === 'device' && !isTemplatedDevice(prop.node)"
                  size="sm"
                  round
                  color="info"
                  icon="sensors"
                  @click.stop="emit('addPointToDevice', prop.node)"
                >
                  <q-tooltip>Add Point to Device</q-tooltip>
                </q-btn>

                <!-- Restricted Point Button for Templated Devices -->
                <q-btn
                  v-if="prop.node.type === 'device' && isTemplatedDevice(prop.node)"
                  size="sm"
                  round
                  color="grey-5"
                  icon="sensors"
                  disable
                  @click.stop="emit('showTemplateRestriction', prop.node)"
                >
                  <q-tooltip>Points managed by template - Click template button to edit</q-tooltip>
                </q-btn>

                <!-- Restricted Device/Point Buttons for Templated Equipment (exclude Controllers) -->
                <q-btn
                  v-if="prop.node.type === 'equipment' && !isController(prop.node) && isTemplatedEquipment(prop.node)"
                  size="sm"
                  round
                  color="grey-5"
                  icon="memory"
                  disable
                  @click.stop="emit('showTemplateRestriction', prop.node)"
                >
                  <q-tooltip>Devices managed by template - Click to edit template</q-tooltip>
                </q-btn>

                <q-btn
                  v-if="prop.node.type === 'equipment' && !isController(prop.node) && isTemplatedEquipment(prop.node)"
                  size="sm"
                  round
                  color="grey-5"
                  icon="sensors"
                  disable
                  @click.stop="emit('showTemplateRestriction', prop.node)"
                >
                  <q-tooltip>Points managed by template - Click to edit template</q-tooltip>
                </q-btn>

                <q-btn
                  v-if="prop.node.type === 'switchboard'"
                  size="sm"
                  round
                  color="secondary"
                  icon="edit"
                  @click.stop="emit('editSwitchboard', prop.node.data)"
                >
                  <q-tooltip>Edit Switchboard</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="prop.node.type === 'switchboard' && prop.node.data.type === 'DDC_Panel'"
                  size="sm"
                  round
                  color="info"
                  icon="memory"
                  @click.stop="emit('viewSwitchboardPoints', prop.node.data)"
                >
                  <q-tooltip>View DDC Points</q-tooltip>
                </q-btn>
                <!-- Point Actions -->
                <q-btn
                  v-if="prop.node.type === 'point' && !isTemplatedPoint(prop.node)"
                  size="sm"
                  round
                  color="info"
                  icon="edit"
                  @click.stop="emit('editPoint', prop.node)"
                >
                  <q-tooltip>Edit Point</q-tooltip>
                </q-btn>

                <!-- Disabled Point Edit for Templated Points -->
                <q-btn
                  v-if="prop.node.type === 'point' && isTemplatedPoint(prop.node)"
                  size="sm"
                  round
                  color="grey-5"
                  icon="edit"
                  disable
                  @click.stop="emit('showTemplateRestriction', prop.node)"
                >
                  <q-tooltip>Point managed by template</q-tooltip>
                </q-btn>
                <!-- Generic Delete Button (exclude controllers - they have their own delete button) -->
                <q-btn
                  v-if="prop.node.type !== 'site' && canDeleteNode(prop.node) && !isController(prop.node)"
                  size="sm"
                  round
                  color="negative"
                  icon="delete"
                  @click.stop="emit('confirmDelete', prop.node)"
                >
                  <q-tooltip>Delete</q-tooltip>
                </q-btn>
              </div>
            </div>
          </template>
        </q-tree>

        <div v-if="!props.treeNodes.length && !props.loadingHierarchy" class="text-center q-pa-xl text-grey">
          <q-icon name="account_tree" size="3em" class="q-mb-md" />
          <div class="text-h6">No Hierarchy Data</div>
          <div class="text-body2">Add buildings and levels to get started</div>
        </div>

        <!-- Loading Overlay -->
        <q-inner-loading :showing="props.loadingHierarchy" style="background-color: rgba(255, 255, 255, 0.9);">
          <q-spinner size="50px" color="primary" />
          <div class="text-primary q-mt-md">Loading hierarchy...</div>
        </q-inner-loading>
      </div>

      <!-- Right Side: Details Sidebar -->
      <details-side-panel
        :model-value="shouldShowDetailsSidebar"
      >
        <hierarchy-details-sidebar
          :selected-item="selectedHierarchyItem"
          :get-documentation-percentage="getDocumentationPercentage"
          @refresh="emit('refresh')"
          @add-level="emit('addLevel', $event)"
          @add-space="emit('addSpace', $event)"
          @add-equipment-to-space="emit('addEquipmentToSpace', $event)"
          @add-switchboard-to-space="emit('addSwitchboardToSpace', $event)"
          @show-space-summary="emit('showSpaceSummary', $event)"
          @navigate-to-equipment="navigateToEquipment"
          @delete-building="emit('confirmDelete', $event)"
          @delete-level="emit('confirmDelete', $event)"
          @delete-space="emit('confirmDelete', $event)"
          @delete-switchboard="emit('confirmDelete', $event)"
        />
      </details-side-panel>
    </div>
  </div>
</template>

<script setup>
  import { defineProps, defineEmits, watch, onMounted, computed, nextTick } from 'vue';
  import BrickStatsPanel from '@/components/brick/BrickStatsPanel.vue';
  import DetailsSidePanel from '@/components/common/DetailsSidePanel.vue';
  import HierarchyDetailsSidebar from '../HierarchyDetailsSidebar.vue';

  const props = defineProps({
    treeNodes: {
      type: Array,
      required: true,
    },
    expandedNodes: {
      type: Array,
      required: true,
    },
    selectedNode: {
      type: String,
      default: '',
    },
    hierarchyStats: {
      type: Object,
      required: true,
    },
    loadingHierarchy: {
      type: Boolean,
      default: false,
    },
    quoteContext: {
      type: Object,
      default: null,
    },
    lifecycleFilter: {
      type: String,
      default: 'installed',
    },
    // Helper functions passed as props
    // NOTE: All function props are non-required with safe defaults to prevent timing race conditions
    // during component initialization. Parent will provide actual implementations.
    isCompoundDevice: {
      type: Function,
      required: false,
      default: () => () => false,
    },
    getCompoundDeviceTooltip: {
      type: Function,
      required: false,
      default: () => () => '',
    },
    isTemplatedEquipment: {
      type: Function,
      required: false,
      default: () => () => false,
    },
    isTemplatedDevice: {
      type: Function,
      required: false,
      default: () => () => false,
    },
    isTemplatedPoint: {
      type: Function,
      required: false,
      default: () => () => false,
    },
    hasTemplateUpdate: {
      type: Function,
      required: false,
      default: () => () => false,
    },
    getTemplateTooltip: {
      type: Function,
      required: false,
      default: () => () => '',
    },
    getTemplateInfo: {
      type: Function,
      required: false,
      default: () => () => null,
    },
    isZoneSection: {
      type: Function,
      required: false,
      default: () => () => false,
    },
    getDocumentationStatusColor: {
      type: Function,
      required: false,
      default: () => () => 'grey',
    },
    getDocumentationStatusIcon: {
      type: Function,
      required: false,
      default: () => () => 'description',
    },
    getDocumentationStatus: {
      type: Function,
      required: false,
      default: () => () => '',
    },
    shouldShowSpaceBadge: {
      type: Function,
      required: false,
      default: () => () => false,
    },
    getSpaceSummaryChipColor: {
      type: Function,
      required: false,
      default: () => () => 'grey',
    },
    getSpaceSummaryIcon: {
      type: Function,
      required: false,
      default: () => () => 'info',
    },
    getSpaceSummaryText: {
      type: Function,
      required: false,
      default: () => () => '',
    },
    getSpaceMetadataBadgeColor: {
      type: Function,
      required: false,
      default: () => () => 'grey',
    },
    getSpaceMetadataBadgeLabel: {
      type: Function,
      required: false,
      default: () => () => '',
    },
    getSpaceMetadataTooltip: {
      type: Function,
      required: false,
      default: () => () => '',
    },
    getDocumentationButtonColor: {
      type: Function,
      required: false,
      default: () => () => 'grey',
    },
    getDocumentationPercentage: {
      type: Function,
      required: false,
      default: () => () => null,
    },
    getDocumentationBadgeColor: {
      type: Function,
      required: false,
      default: () => () => 'grey',
    },
    getDocumentationMissing: {
      type: Function,
      required: false,
      default: () => () => '',
    },
    canAddPointsToEquipment: {
      type: Function,
      required: false,
      default: () => () => false,
    },
    isController: {
      type: Function,
      required: false,
      default: () => () => false,
    },
    isAHU: {
      type: Function,
      required: false,
      default: () => () => false,
    },
    canDeleteNode: {
      type: Function,
      required: false,
      default: () => () => false,
    },
  });

  // NOTE: Props are accessed via props.* in template to maintain reactivity
  // Destructuring props loses reactivity in Vue 3 Composition API
  const {
    // Functions can be safely destructured (they don't change)
    isCompoundDevice,
    getCompoundDeviceTooltip,
    isTemplatedEquipment,
    isTemplatedDevice,
    isTemplatedPoint,
    hasTemplateUpdate,
    getTemplateTooltip,
    getTemplateInfo,
    isZoneSection,
    getDocumentationStatusColor,
    getDocumentationStatusIcon,
    getDocumentationStatus,
    shouldShowSpaceBadge,
    getSpaceSummaryChipColor,
    getSpaceSummaryIcon,
    getSpaceSummaryText,
    getSpaceMetadataBadgeColor,
    getSpaceMetadataBadgeLabel,
    getSpaceMetadataTooltip,
    getDocumentationButtonColor,
    getDocumentationPercentage,
    getDocumentationBadgeColor,
    getDocumentationMissing,
    canAddPointsToEquipment,
    isController,
    isAHU,
    canDeleteNode,
  } = props;

  const emit = defineEmits([
    'markAsReplacement',
    'update:expandedNodes',
    'update:selectedNode',
    'addBuilding',
    'editBuilding',
    'addLevel',
    'editLevel',
    'addSpace',
    'editSpace',
    'addEquipmentToSpace',
    'addSwitchboardToSpace',
    'showControllerIoDialog',
    'addDamperToZone',
    'addPointToZone',
    'addActuatorToZone',
    'saveZoneAsTemplate',
    'duplicateZone',
    'addDeviceToZone',
    'editEquipment',
    'showDocumentation',
    'showTemplateActions',
    'addDevice',
    'addPointToEquipment',
    'addZoneSection',
    'editDevice',
    'addPointToDevice',
    'showTemplateRestriction',
    'editSwitchboard',
    'viewSwitchboardPoints',
    'editPoint',
    'switchToElectricalTab',
    'selectElectricalNode',
    'confirmDelete',
    'contextMenu',
    'showSpaceSummary',
  ]);

  // Lifecycle helper functions
  const getLifecycleColor = (stage) => {
    const colors = {
      quote: 'amber',
      project: 'blue',
      installed: 'green',
      maintenance: 'orange',
      decommissioned: 'grey',
    };
    return colors[stage] || 'grey';
  };

  const getLifecycleIcon = (stage) => {
    const icons = {
      quote: 'request_quote',
      project: 'engineering',
      installed: 'check_circle',
      maintenance: 'build',
      decommissioned: 'cancel',
    };
    return icons[stage] || 'help';
  };

  const getLifecycleLabel = (stage) => {
    const labels = {
      quote: 'Quote',
      project: 'Project',
      installed: 'Installed',
      maintenance: 'Maintenance',
      decommissioned: 'Decommissioned',
    };
    return labels[stage] || stage;
  };

  // Check if equipment belongs to current quote
  const belongsToCurrentQuote = (node) => {
    // Not in quote context - show all at full opacity
    if (!props.quoteContext) {return true;}

    // Not equipment type - show at full opacity
    if (node.type !== 'equipment') {return true;}

    // No data available - show at reduced opacity (safer default)
    if (!node.data) {return false;}

    // Check if this equipment's quote_id matches current quote
    return node.data.quote_id === props.quoteContext.quote_id;
  };

  // Computed properties for details sidebar
  const shouldShowDetailsSidebar = computed(() => {
    if (!props.selectedNode) {return false;}
    const selected = findSelectedNode(props.treeNodes, props.selectedNode);
    return selected && ['building', 'level', 'space', 'switchboard'].includes(selected.type);
  });

  const selectedHierarchyItem = computed(() => {
    if (!props.selectedNode) {return null;}
    return findSelectedNode(props.treeNodes, props.selectedNode);
  });

  // Helper function to find selected node in tree
  function findSelectedNode(nodes, id) {
    for (const node of nodes) {
      if (node.id === id) {return node;}
      if (node.children) {
        const found = findSelectedNode(node.children, id);
        if (found) {return found;}
      }
    }
    return null;
  }

  // Navigate to equipment when clicked from documentation table
  function navigateToEquipment(equipmentNode) {
    // All equipment types now open the documentation dialog
    // (including switchboards, DDC boards, controllers)
    emit('showDocumentation', equipmentNode);
  }
</script>

<style scoped>
.other-quote-equipment {
  opacity: 0.6;
}
</style>
